import { z } from 'zod';

import { IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * HotelInventory (B4.12) - per-Hotel rate cards.
 *
 * One row per (Hotel, RoomType, MealPlan, OccupancyType, validity window)
 * tuple. Sub-resource of Hotel; mounted at `/hotels/:hotelId/inventory`.
 *
 * Prices are plain decimal amounts (up to 2 places) in the row's own
 * currency (`currencyId`) - no minor units / "paise".
 */

/** A money amount: non-negative number with up to 2 decimal places. */
const PriceSchema = z.number().nonnegative();

/** Meal option a price period sells with - per-person per-night supplement. */
export const HotelInventoryMealOptionSchema = z.object({
  id: UuidSchema,
  mealPlanId: UuidSchema,
  mealPlanCode: z.string().nullable(),
  mealPlanName: z.string().nullable(),
  perAdult: z.number(),
  perChild: z.number(),
  isActive: z.boolean(),
});
export type HotelInventoryMealOption = z.infer<typeof HotelInventoryMealOptionSchema>;

export const HotelInventoryMealOptionInputSchema = z.object({
  mealPlanId: UuidSchema,
  perAdult: PriceSchema.optional(),
  perChild: PriceSchema.optional(),
  isActive: z.boolean().optional(),
});
export type HotelInventoryMealOptionInput = z.infer<typeof HotelInventoryMealOptionInputSchema>;

export const HotelInventoryListItemSchema = z.object({
  id: UuidSchema,
  hotelId: UuidSchema,
  legacyInventoryId: z.number().int().nullable(),
  legacyInventoryCode: z.string().nullable(),
  hotelRoomId: UuidSchema.nullable(),
  hotelRoomName: z.string().nullable(),
  roomTypeId: UuidSchema.nullable(),
  roomTypeName: z.string().nullable(),
  mealPlanId: UuidSchema.nullable(),
  mealPlanName: z.string().nullable(),
  occupancyTypeId: UuidSchema.nullable(),
  occupancyTypeName: z.string().nullable(),
  /** Base adults the rate covers (occupancy type paxCount). Null = default 2. */
  occupancyPaxCount: z.number().int().nullable(),
  /** Explicit base occupancy (adults) the rate covers - a free integer that
   *  overrides the 1-3 occupancy-type paxCount (villa/houseboat base 4/6/8...).
   *  Null falls back to `occupancyPaxCount`, else 2. */
  baseOccupancy: z.number().int().nullable(),
  /** Live FX rate from this rate's currency to INR at list-time (1 for INR).
   *  Lets the itinerary builder show in-house hotel prices already in rupees. */
  fxRateToInr: z.number().default(1),
  validityFrom: z.string(),
  validityTo: z.string(),
  /** Day-of-week applicability mask. bit0=Sun..bit6=Sat; 127 = all days. */
  weekdayMask: z.number().int(),
  currencyId: UuidSchema.nullable(),
  currencyCode: z.string().nullable(),
  adultPrice: z.number(),
  childWithBedPrice: z.number(),
  childWithoutBedPrice: z.number(),
  infantPrice: z.number(),
  extraAdultPrice: z.number(),
  extraChildPrice: z.number(),
  extraBedChildPrice: z.number(),
  fixedRoomCost: z.number(),
  /** Sold-out / unavailable dates within the window (YYYY-MM-DD). */
  blackoutDates: z.array(z.string()),
  mealOptions: z.array(HotelInventoryMealOptionSchema),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type HotelInventoryListItem = z.infer<typeof HotelInventoryListItemSchema>;

export const HotelInventoryListQuerySchema = PaginationQuerySchema.extend({
  hotelRoomId: UuidSchema.optional(),
  roomTypeId: UuidSchema.optional(),
  mealPlanId: UuidSchema.optional(),
  occupancyTypeId: UuidSchema.optional(),
  /** Filter to rows whose validity window contains this date (YYYY-MM-DD). */
  onDate: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/)
    .optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type HotelInventoryListQuery = z.infer<typeof HotelInventoryListQuerySchema>;

export const HotelInventoryListResponseSchema = paginatedResponseSchema(
  HotelInventoryListItemSchema,
);
export type HotelInventoryListResponse = z.infer<typeof HotelInventoryListResponseSchema>;

const HotelInventoryCoreFieldsSchema = z.object({
  hotelRoomId: UuidSchema.nullable().optional(),
  roomTypeId: UuidSchema.nullable().optional(),
  mealPlanId: UuidSchema.nullable().optional(),
  occupancyTypeId: UuidSchema.nullable().optional(),
  /** Base occupancy (adults) the rate covers - free integer; null clears it. */
  baseOccupancy: z.number().int().min(1).max(40).nullable().optional(),
  validityFrom: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  validityTo: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  /** bit0=Sun..bit6=Sat; defaults to all days (127) when omitted. */
  weekdayMask: z.number().int().min(0).max(127).optional(),
  currencyId: UuidSchema.nullable().optional(),
  adultPrice: PriceSchema.optional(),
  childWithBedPrice: PriceSchema.optional(),
  childWithoutBedPrice: PriceSchema.optional(),
  infantPrice: PriceSchema.optional(),
  extraAdultPrice: PriceSchema.optional(),
  extraChildPrice: PriceSchema.optional(),
  extraBedChildPrice: PriceSchema.optional(),
  fixedRoomCost: PriceSchema.optional(),
  /** Sold-out / unavailable dates within the window (YYYY-MM-DD). */
  blackoutDates: z
    .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
    .max(366)
    .optional(),
  /** Full replacement set of meal options (sync semantics). */
  mealOptions: z.array(HotelInventoryMealOptionInputSchema).optional(),
  isActive: z.boolean().optional(),
});

export const CreateHotelInventoryRequestSchema = HotelInventoryCoreFieldsSchema;
export type CreateHotelInventoryRequest = z.infer<typeof CreateHotelInventoryRequestSchema>;

export const UpdateHotelInventoryRequestSchema = HotelInventoryCoreFieldsSchema.partial();
export type UpdateHotelInventoryRequest = z.infer<typeof UpdateHotelInventoryRequestSchema>;
